import { memo, useEffect, useCallback, useState } from "react";
import Form from "../../components/Form.js";
import Table from "../../components/Table.js";
import { useSnackbar } from "../../utils/index.js";
import Spinner from "../../components/Spinner.js";
import { Box, Typography, Grid2, Button } from "@mui/material";
import { getOwners, getLambScrapies } from "../../api/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import {
    lambSlugHeaderNoFilterable,
    damCodeHeaderNoClick,
    sireCodeHeaderNoClick,
    sexHeaderNoFilter,
    birthDateHeaderNotFilterable,
    sampleNumberHeader,
    firstResultHeader,
    secondResultHeader,
    authenticityHeader,
    sampleDateHeader,
    resultDateHeader,
} from "../../components/TableHeaders.js";


const ViewLambScrapie = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [step, setStep] = useState(1);
    const [selectedOwner, setSelectedOwner] = useState(null);
    const [fetchedLambsScrapies, setFetchedLambsScrapies] = useState([]);

    const listClasses = useListStyles();
    const selectClasses = useSelectStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const { owners } = await getOwners();
            setFetchedOwners(owners);
            setOwnersDropdownValues(
                owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                }))
            );
        } catch (err) {
            error(err);
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const ownerDatesFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "date-picker",
            id: "startLambingDate",
            type: "desktop",
            label: "Έναρξη περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "date-picker",
            id: "endLambingDate",
            type: "desktop",
            label: "Τέλος περιόδου τοκετών",
            width: "595px",
            background: "third",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Υποβολή",
        },
    ];

    const lambsScrapieTableColumns = [
        lambSlugHeaderNoFilterable,
        damCodeHeaderNoClick,
        sireCodeHeaderNoClick,
        sexHeaderNoFilter,
        birthDateHeaderNotFilterable,
        sampleNumberHeader,
        firstResultHeader,
        secondResultHeader,
        authenticityHeader,
        sampleDateHeader,
        resultDateHeader,
    ]

    const handleOwnerDateSelect = async (values) => {
        setIsLoading(true);
        const selectedOwnerObj = fetchedOwners.find(
            (owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner
        );
        setSelectedOwner(selectedOwnerObj);
        const startLambingDate = values.startLambingDate;
        const endLambingDate = values.endLambingDate;
        const { success: scs, scrapies, scrapieCodes, message } = await getLambScrapies(
            selectedOwnerObj._id,
            startLambingDate,
            endLambingDate
        );
        if (scs) {
            const tableData = [];
            for (const scrapie of scrapies) {
                tableData.push({
                    lambSlug: scrapie.lambSlug,
                    damCode: scrapie.animalId.damCode,
                    sireCode: scrapie.animalId.sireCode,
                    sex: scrapie.animalId.sex,
                    birthDate: scrapie.animalId.birthDate,
                    sampleCode: scrapie.sampleCode,
                    sampleDate: scrapie.sampleDate,
                    resultDate: scrapie.resultDate,
                    result1: scrapieCodes.find((code) => Number(code.scrapieCode) === scrapie.result1)?.scrapieName,
                    result2: scrapieCodes.find((code) => Number(code.scrapieCode) === scrapie.result2)?.scrapieName,
                    authenticity: scrapie.authenticity,
                });
            }
            setFetchedLambsScrapies(tableData);
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <Box className={selectClasses.outerBox}>
                    <Box container flexDirection="column" className={selectClasses.innerBox}>
                        <Typography variant="h5" sx={{ margin: "1rem" }}>
                            Επιλέξτε την Εκτροφή και τις ημερομηνίες που σας ενδιαφέρουν:
                        </Typography>
                        <Form
                            content={ownerDatesFormContent}
                            onSubmit={handleOwnerDateSelect}
                            toResetForm={true}
                        />
                    </Box>
                </Box>
            )}
            {step === 2 && (
                <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", display: "flex", alignItems: "center" }}>
                    <Box display="flex" justifyContent="center" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                        <Typography variant="h5" className={listClasses.form_title} sx={{ display: "flex", alignItems: "center" }}>
                            {`Εμφάνιση Στοιχείων SCRAPIE για τη μονάδα ${selectedOwner?.lastName} ${selectedOwner?.firstName}`}
                        </Typography>
                    </Box>
                    <Table
                        data={fetchedLambsScrapies}
                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν Εγγραφές!"}
                        columns={lambsScrapieTableColumns}
                    />
                </Grid2>
            )}
            {step !== 1 && (
                <Box sx={{ width: "100%", marginTop: "2rem", display: "flex", justifyContent: "center" }}>
                    <Button variant="outlined" color="secondary" onClick={() => setStep(step - 1)}>
                        Πίσω
                    </Button>
                </Box>
            )}
        </>
    )
}

export default memo(ViewLambScrapie);